/* eslint-disable prettier/prettier */
import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import GlobalStyles from '../GlobalStyles'
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'
import {
  ThemeProvider as MuiThemeProvider,
  StylesProvider,
  createGenerateClassName
} from '@material-ui/styles'
import ThemeContext from '../Context'
/* if-not-on-site-start */
import { useAppDarkMode } from '@signal/theme/hooks/useAppDarkMode'
/* if-not-on-site-end */
import DarkModeContext from '@signal/theme/Context/dark-mode'
import { replaceThemeColors } from '@signal/core/tenantTheme/utils'
import { 
  /* if-not-on-site-start */
  makeDarkTheme,
  /* if-not-on-site-end */
  makeLightTheme
} from '@signal/theme/theme'
import isTest from '@signal/utils/env/isTest'

const defaultGenerateClassName = createGenerateClassName()

export function ThemeProvider(props) {
  const {
    /* if-not-on-site-start */
    tenantTheme,
    /* if-not-on-site-end */
    generateClassName = defaultGenerateClassName,
    children,
    onSiteShadowRootChild
  } = props
  /* if-not-on-site-start */
  const { isDarkMode, setIsDarkMode } = useAppDarkMode(tenantTheme)
  const darkTheme = makeDarkTheme()
  /* if-not-on-site-end */
  const lightTheme = makeLightTheme()
  const defaultTheme = useMemo(
    () => ({
      // it is important to clone this object each time, to force MUI to lay down new style tags when the theme
      // changes.  The fact this wasn't always happening, because MUI thought it was clever enough to not always need
      // to apply new styles when changing between dark mode and light mode led to some of the weird leftover colors
      // seen when toggling dark mode in some situations
      ...(
        /* if-not-on-site-start */
        tenantTheme && !tenantTheme.isDefaultTheme
          ? replaceThemeColors(lightTheme, tenantTheme)
          : isDarkMode && !onSiteShadowRootChild
          ? darkTheme :
          /* if-not-on-site-end */
            ({
               ...lightTheme,
               props: onSiteShadowRootChild ? {
                 ...lightTheme.props,
                 MuiPopover: {
                   container: onSiteShadowRootChild,
                 },
                 MuiPopper: {
                   container: onSiteShadowRootChild,
                 },
                 MuiModal: {
                   container: onSiteShadowRootChild,
                 }
              } : lightTheme.props,       
            })
      )
    }),
    [
      onSiteShadowRootChild,
      lightTheme,
      /* if-not-on-site-start */
      darkTheme,
      isDarkMode,
      tenantTheme
      /* if-not-on-site-end */
    ]
  )

  const [values, setValues] = useState(defaultTheme)

  const themeContext = useMemo(
    () => ({
      setValues,
      values
    }),
    [setValues, values]
  )

  const darkModeContext = useMemo(
    () => {
      /* if-not-on-site-start */
      return {
        isDarkMode,
        canUseDarkMode: tenantTheme ? tenantTheme.isDefaultTheme : true,
        setIsDarkMode
      }
      /* if-not-on-site-end */
      return {
        isDarkMode: false,
        canUseDarkMode: false,
        setIsDarkMode: () => {}
      }
    },
    [
      /* if-not-on-site-start */
      isDarkMode,
      setIsDarkMode,
      tenantTheme
      /* if-not-on-site-end */
    ]
  )

  return (
    <DarkModeContext.Provider value={darkModeContext}>
      <ThemeContext.Provider value={themeContext}>
        <StylesProvider generateClassName={generateClassName} injectFirst={!isTest() && !onSiteShadowRootChild}>
          <MuiThemeProvider theme={defaultTheme}>
            <StyledThemeProvider theme={defaultTheme}>
              <EmotionThemeProvider theme={defaultTheme}>
                <>
                  <GlobalStyles
                    background={values.background}
                    forOnSite={!!onSiteShadowRootChild}
                  />
                  {children}
                </>
              </EmotionThemeProvider>
            </StyledThemeProvider>
          </MuiThemeProvider>
        </StylesProvider>
      </ThemeContext.Provider>
    </DarkModeContext.Provider>
  )
}

ThemeProvider.propTypes = {
  tenantTheme: PropTypes.object,
  generateClassName: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node)
  ])
}

export default ThemeProvider
